/* eslint-disable max-lines */
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import Input from '@components/atoms/ui-kit/input.tsx';
import SelectInput from '@components/atoms/ui-kit/select-input.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormReset
} from 'react-hook-form';
import { Dispatch, SetStateAction, useMemo } from 'react';
import {
  CreateWebhookPayload,
  useCreateWebhook
} from '@utils/api/back-office/webhooks/use-create-webhook.tsx';
import { Params, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base.ts';

type Props = {
  createModalOpen: boolean;
  setCreateModalOpen: Dispatch<SetStateAction<boolean>>;
  handleSubmit: UseFormHandleSubmit<CreateWebhookPayload, CreateWebhookPayload>;
  reset: UseFormReset<CreateWebhookPayload>;
  errors: FieldErrors<CreateWebhookPayload>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<CreateWebhookPayload, any>;
  register: UseFormRegister<CreateWebhookPayload>;
  getValues: UseFormGetValues<CreateWebhookPayload>;
};

export default function CreateNewWebhookForm({
  createModalOpen,
  setCreateModalOpen,
  handleSubmit,
  errors,
  reset,
  control,
  getValues,
  register
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const { customerId } = useParams<Params>();

  const { mutate: createWebhook, isLoading } = useCreateWebhook(
    Number(customerId)
  );

  const onSubmit = (data: CreateWebhookPayload) => {
    const headers = getValues('webhook_headers');
    const parsedHeaders =
      typeof headers === 'string' ? JSON.parse(headers) : headers || [];

    const payload: CreateWebhookPayload = {
      ...data,
      organization_id: customerId,
      webhook_headers: parsedHeaders
    };

    createWebhook(payload, {
      onSuccess: () => {
        toast.success(t('webhook-created-successfully'));
        setCreateModalOpen(false);
        reset(
          {
            type: '',
            webhook_url: '',
            webhook_method: '',
            webhook_headers: []
          },
          { keepErrors: false }
        );
      },
      onError: (error: AxiosError<BaseError>) => {
        const data = error.response?.data;
        const detail = data?.detail;
        const detailMessage = Array.isArray(detail)
          ? detail
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .map((d: any) => d?.msg || d?.message)
              .filter(Boolean)
              .join('; ')
          : typeof detail === 'string'
            ? detail
            : undefined;

        const errorMessage =
          data?.error_description ||
          detailMessage ||
          data?.error ||
          data?.message ||
          t('an-error-occurred-while-fetching-the-webhook');

        toast.error(errorMessage);
      }
    });
  };

  const styles = useMemo(
    () =>
      StyleSheet.create({
        card: {
          backgroundColor: getColour('appSoft'),
          border: `1px solid ${getColour('hairline')}`,
          borderRadius: 16
        },
        cardHeader: {
          borderBottom: `1px solid ${getColour('hairline')}`
        },
        subtle: {
          color: getColour('icon')
        },
        label: {
          color: getColour('normalText')
        },
        textField: {
          backgroundColor: getColour('appSoft'),
          borderColor: getColour('hairline'),
          color: getColour('normalText')
        },
        headerRow: {
          backgroundColor: getColour('thirdLayer'),
          borderColor: getColour('hairline')
        }
      }),
    [getColour]
  );

  const webhookMethodOptions = [
    { value: 'POST', label: 'POST' },
    { value: 'PUT', label: 'PUT' }
  ];

  const webhookTypeOptions = [
    { value: 'REVIEWER_CALLBACK', label: 'REVIEWER_CALLBACK' },
    { value: 'ADMIN_CALLBACK', label: 'ADMIN_CALLBACK' },
    {
      value: 'ADMIN_FROM_SESSION_CALLBACK',
      label: 'ADMIN_FROM_SESSION_CALLBACK'
    },
    {
      value: 'EXCEEDANCE_NOTIFICATION',
      label: 'EXCEEDANCE_NOTIFICATION'
    }
  ];

  return (
    <Modal
      isOpen={createModalOpen}
      onClose={() => {
        setCreateModalOpen(false);
        reset(
          {
            type: '',
            webhook_url: '',
            webhook_method: '',
            webhook_headers: []
          },
          { keepErrors: false }
        );
      }}
      size="medium"
      title={t('create-new-webhook')}
      caption={t('webhook-create-description')}
      footerActions={[
        <Button
          onClick={() => setCreateModalOpen(false)}
          label={t('cancel')}
          size="small"
          variant="smoke"
          key="cancel"
          disabled={isLoading}
        />,
        <Button
          key="create"
          type="submit"
          onClick={handleSubmit(onSubmit)}
          label={isLoading ? `${t('creating')}...` : t('create')}
          size="small"
          variant="primary"
          disabled={isLoading}
        />
      ]}
    >
      {/* keep submit wiring exactly as before */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* CONFIG CARD (consistent with template modals) */}
        <div className={cn(css(styles.card), 'overflow-hidden')}>
          <div className={cn(css(styles.cardHeader), 'px-4 py-3')}>
            <p className={cn(css(styles.label), 'text-sm font-semibold')}>
              {t('webhook-configuration') || 'Webhook configuration'}
            </p>
            <p className={cn(css(styles.subtle), 'text-xs mt-0.5')}>
              {t('webhook-configuration-hint') ||
                'Choose when it triggers, where it sends data, and the HTTP method.'}
            </p>
          </div>

          <div className="p-4 md:p-5 space-y-4">
            {/* Type */}
            <div className="space-y-1.5">
              <label
                htmlFor="type"
                className={cn(css(styles.label), 'block text-sm font-medium')}
              >
                {t('webhook-type')} <span className="text-red-500">*</span>
              </label>

              <Controller
                control={control}
                name="type"
                render={({ field: { onChange, value } }) => (
                  <SelectInput
                    id="type"
                    name="type"
                    value={
                      webhookTypeOptions.find(
                        option => option.value === value
                      ) || null
                    }
                    onChange={selectedOption => {
                      onChange(selectedOption ? selectedOption.value : null);
                    }}
                    options={webhookTypeOptions}
                    isMulti={false}
                    error={errors.type && errors.type.message}
                  />
                )}
              />

              <p className={cn(css(styles.subtle), 'text-xs')}>
                {t('webhook-type-hint') ||
                  'Select when this webhook should be triggered'}
              </p>
            </div>

            {/* URL */}
            <div className="space-y-1.5">
              <Input
                id="webhook_url"
                label={
                  <>
                    {t('webhook-url')} <span className="text-red-500">*</span>
                  </>
                }
                type="text"
                placeholder="https://your-domain.com/webhook"
                autoComplete="webhook-url"
                {...register('webhook_url')}
                error={errors?.webhook_url?.message}
              />
              <p className={cn(css(styles.subtle), 'text-xs')}>
                {t('webhook-url-hint') ||
                  'The endpoint URL where webhook data will be sent'}
              </p>
            </div>

            {/* Method */}
            <div className="space-y-1.5">
              <label
                htmlFor="webhook_method"
                className={cn(css(styles.label), 'block text-sm font-medium')}
              >
                {t('http-method')} <span className="text-red-500">*</span>
              </label>

              <Controller
                control={control}
                name="webhook_method"
                render={({ field: { onChange, value } }) => (
                  <SelectInput
                    id="webhook_method"
                    name="webhook_method"
                    value={
                      webhookMethodOptions.find(
                        option => option.value === value
                      ) || null
                    }
                    onChange={selectedOption => {
                      onChange(selectedOption ? selectedOption.value : null);
                    }}
                    options={webhookMethodOptions}
                    isMulti={false}
                    error={
                      errors.webhook_method && errors.webhook_method.message
                    }
                  />
                )}
              />

              <p className={cn(css(styles.subtle), 'text-xs')}>
                {t('http-method-hint') ||
                  'The HTTP method to use when calling the webhook'}
              </p>
            </div>
          </div>
        </div>

        {/* HEADERS CARD (clean separation + better alignment) */}
        <div className={cn(css(styles.card), 'overflow-hidden')}>
          <div className={cn(css(styles.cardHeader), 'px-4 py-3')}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className={cn(css(styles.label), 'text-sm font-semibold')}>
                  {t('custom-headers')}{' '}
                  {t('optional') ? (
                    <span className={cn(css(styles.subtle), 'font-normal')}>
                      ({t('optional')})
                    </span>
                  ) : null}
                </p>
                <p className={cn(css(styles.subtle), 'text-xs mt-0.5')}>
                  {t('webhook-headers-hint') ||
                    'Add custom HTTP headers to include with each webhook request. Format: Header-Name: Header-Value'}
                </p>
              </div>
              <span
                className={cn(
                  css(styles.subtle),
                  'text-[11px] px-2 py-1 rounded-full border whitespace-nowrap'
                )}
                style={{ borderColor: getColour('hairline') }}
              >
                {t('advanced') || 'Advanced'}
              </span>
            </div>
          </div>

          <div className="p-4 md:p-5">
            <Controller
              name="webhook_headers"
              control={control}
              render={({ field: { onChange, value } }) => {
                const headers: string[] = Array.isArray(value) ? value : [];

                const handleAddHeader = () => {
                  onChange([...headers, '']);
                };

                const handleHeaderChange = (index: number, newVal: string) => {
                  const updated = [...headers];
                  updated[index] = newVal;
                  onChange(updated);
                };

                const handleRemoveHeader = (index: number) => {
                  const updated = [...headers];
                  updated.splice(index, 1);
                  onChange(updated);
                };

                return (
                  <div className="space-y-3">
                    {headers.length === 0 ? (
                      <div
                        className={cn(
                          'text-sm italic py-3 px-4 rounded-xl border',
                          css(styles.subtle)
                        )}
                        style={{ borderColor: getColour('hairline') }}
                      >
                        {t('no-headers-added') ||
                          'No custom headers configured'}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {headers.map((header, idx) => (
                          <div
                            key={idx}
                            className={cn(
                              'flex items-center gap-2 rounded-xl border p-3',
                              css(styles.headerRow)
                            )}
                            style={{ borderColor: getColour('hairline') }}
                          >
                            <div className="flex-1 min-w-0">
                              <input
                                type="text"
                                value={header}
                                placeholder="Authorization: Bearer your-token-here"
                                onChange={e =>
                                  handleHeaderChange(idx, e.target.value)
                                }
                                className={cn(
                                  'w-full px-3 py-2 rounded-2xl border text-sm focus:outline-none',
                                  'focus:ring-2 focus:ring-primary',
                                  'shadow-sm focus:shadow',
                                  'min-h-[40px]'
                                )}
                                style={{
                                  backgroundColor: getColour('appSoft'),
                                  borderColor: getColour('hairline'),
                                  color: getColour('normalText')
                                }}
                              />
                            </div>

                            <button
                              type="button"
                              onClick={() => handleRemoveHeader(idx)}
                              className="text-red-500 hover:text-red-700 text-sm font-medium px-2 py-2 rounded-2xl"
                            >
                              {t('remove')}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={handleAddHeader}
                      className="text-sm font-medium hover:opacity-80"
                      style={{ color: getColour('activeText') }}
                    >
                      + {t('add-header')}
                    </button>

                    {errors.webhook_headers && (
                      <p className="text-red-500 text-sm mt-2">
                        {errors.webhook_headers.message}
                      </p>
                    )}
                  </div>
                );
              }}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
}
